import {Checkbox, FormControlLabel, SxProps, TextField, Tooltip, Grid} from "@mui/material";
import React, {useEffect, useState} from "react";
import {tipoPrimitivo} from "@models/solicitacoes/dto.ts";
import dayjs, {Dayjs} from "dayjs";
import {DatePicker} from "@mui/x-date-pickers";
import {ToMoney} from "@shared/MathHelp.ts";

export interface InputEntryProps {
	primitiveType: tipoPrimitivo,
	value?: string | number | boolean | Dayjs,
	onChange?: (value: string | number | boolean | Dayjs | undefined) => void,
	onEnter?: (value: string | number | boolean | Dayjs | undefined) => void,
	label?: string,
	required?: boolean,
	slotProps?: {
		sx?: Partial<SxProps>
		tooltip?: string,
		textFieldProps?: Partial<React.ComponentProps<typeof TextField>>
	},
	disabled?: boolean,
}

export function InputEntry(props: InputEntryProps) {
	const pt = props.primitiveType

	switch (pt) {
		case 'Bool':
			return <BooleanInputEntry {...props}/>
		case 'Money': {
			const moneyProps = {
				...props,
				value: props.value as number | undefined,
				onChange: props.onChange ? ((v?: number | undefined) => {
					props.onChange!(v as number | undefined);
				}) : undefined,
			}
			return <MoneyInputEntry {...moneyProps}/>
		}
		case 'String':
			return <StringInputEntry {...props} />
		case 'Int':
			return <IntInputEntry {...props} />
		case 'Float':
			return <FloatInputEntry {...props} />
		case 'Percent':
			return <PercentInputEntry {...props} />
		case 'NumberOrPercent':
			return <NumberOrPercentInputEntry {...props} />
		case 'Date':
			return <DateInputEntry {...props} />
	}

	return (<></>)
}

export function BooleanInputEntry(props: Pick<InputEntryProps, 'value' | 'onChange' | 'label' | 'slotProps' | 'required' | 'disabled'>) {
	const {value, onChange, label, required, disabled} = props;

	return (
		<Tooltip title={props.slotProps && props.slotProps.tooltip ? props.slotProps.tooltip : ''}>
			<FormControlLabel control={<Checkbox/>}
			                  label={label}
			                  required={required}
			                  value={label}
			                  checked={value === true || value === 'true' || value === 1}
			                  onChange={(_c, v) => onChange && onChange(v)}
			                  disabled={(disabled || !onChange) ?? false}
			/>
		</Tooltip>
	)
}

export function MoneyInputEntry(props: Pick<InputEntryProps, 'required' | 'label' | 'slotProps' | 'disabled'> & {
	value: number | undefined,
	clamp?: { min?: number, max?: number },
	onChange?: (value: number | undefined) => void,
	onEnter?: (value: number | undefined) => void,
}) {
	const [numericValue, setNumericValue] = useState<number | undefined>(props.value || undefined);
	const [rawInputValue, setRawInputValue] = useState<string>('');
	const [isFocused, setIsFocused] = useState<boolean>(false);
	const inputRef = React.useRef<HTMLInputElement>(null);

	// Format the value for display based on focus state
	const getDisplayValue = () => {
		if (isFocused) {
			// When focused, show the raw input value or the numeric value as a plain number
			return rawInputValue || (numericValue ? numericValue.toString() : '');
		} else {
			// When not focused, show the formatted money value
			return ToMoney(numericValue) || '';
		}
	};

	const handleChange = (c: React.ChangeEvent<HTMLInputElement>) => {
		// Store the raw input value
		const inputValue = c.target.value.replace(/[^\d,.]/g, '');
		setRawInputValue(inputValue);

		// Only allow numeric input, commas, and periods for parsing
		const cleanInput = inputValue.replace(/[^\d,.]/g, '');

		// Check if the input contains a decimal separator
		const hasDecimalSeparator = cleanInput.includes(',') || cleanInput.includes('.');

		// Parse the numeric value from the input
		let newNumericValue: number | undefined;

		if (hasDecimalSeparator) {
			// If it has a decimal separator, parse it directly
			// Replace comma with period for proper parsing
			const normalizedInput = cleanInput.replace(/,/g, '.');
			newNumericValue = normalizedInput ? parseFloat(normalizedInput) : undefined;
		} else {
			// If no decimal separator, treat as whole number
			const cleanValue = cleanInput.replace(/\D/g, '');
			newNumericValue = cleanValue ? parseInt(cleanValue, 10) : undefined;
		}
		if (newNumericValue !== undefined && props.clamp) {
			newNumericValue = Math.max(props.clamp.min || 0, Math.min(props.clamp.max || Infinity, newNumericValue));
		}

		// Update the numeric value and call the onChange callback
		setNumericValue(newNumericValue);
		if (props.onChange) props.onChange(newNumericValue);
	};

	// Handle focus state
	const handleFocus = () => {
		setIsFocused(true);
		// When focusing, set the raw input to the numeric value for easier editing
		setRawInputValue(numericValue ? numericValue.toString() : '');
	};

	const handleBlur = () => {
		setIsFocused(false);
		// When blurring, clear the raw input value
		setRawInputValue('');
	};

	// Update the values when props.value changes externally
	useEffect(() => {
		if (numericValue != props.value) {
			setNumericValue(props.value || undefined);
			if (isFocused) {
				setRawInputValue(props.value ? props.value.toString() : '');
			}
		}
	}, [props.value]);

	return (
		<Tooltip title={props.slotProps && props.slotProps.tooltip ? props.slotProps.tooltip : ''}>
			<TextField fullWidth
			           {...props.slotProps?.textFieldProps}
			           label={props.label ?? ''}
			           required={props.required}
			           onChange={handleChange}
			           onFocus={handleFocus}
			           onBlur={handleBlur}
			           onKeyDown={(e) => {
				           if (e.key === 'Enter' && props.onEnter) {
					           props.onEnter(numericValue);
				           }
			           }}
			           value={getDisplayValue()}
			           disabled={(props.disabled || !props.onChange) ?? false}
			           sx={{...props.slotProps?.sx ?? {}}}
			           inputMode={'numeric'}
			           inputRef={inputRef}
			/></Tooltip>
	);
}


// Generic string input
export function StringInputEntry(props: InputEntryProps) {
	const value = (props.value ?? '') as string;
	return (
		<Tooltip title={props.slotProps && props.slotProps.tooltip ? props.slotProps.tooltip : ''}>
			<TextField fullWidth
			           {...props.slotProps?.textFieldProps}
			           label={props.label ?? ''}
			           required={props.required}
			           value={value}
			           onChange={(e) => props.onChange && props.onChange(e.target.value)}
			           onKeyDown={(e) => {
				           if (e.key === 'Enter' && props.onEnter) props.onEnter(value);
			           }}
			           disabled={(props.disabled || !props.onChange) ?? false}
			           sx={{...props.slotProps?.sx ?? {}}}
			/>
		</Tooltip>
	);
}

// Integer input
export function IntInputEntry(props: InputEntryProps & { clamp?: { min?: number, max?: number } }) {
	const display = props.value !== undefined && props.value !== null ? String(props.value) : '';
	const parseIntOrUndefined = (s: string): number | undefined => {
		const clean = s.replace(/[^\d-]/g, '');
		if (clean === '' || clean === '-') return undefined;
		const n = Number.parseInt(clean, 10);
		if (Number.isNaN(n)) return undefined;
		const min = (props as any).clamp?.min ?? -Infinity;
		const max = (props as any).clamp?.max ?? Infinity;
		return Math.max(min, Math.min(max, n));
	};
	return (
		<Tooltip title={props.slotProps && props.slotProps.tooltip ? props.slotProps.tooltip : ''}>
			<TextField fullWidth
			           {...props.slotProps?.textFieldProps}
			           label={props.label ?? ''}
			           required={props.required}
			           value={display}
			           onChange={(e) => props.onChange && props.onChange(parseIntOrUndefined(e.target.value) as any)}
			           onKeyDown={(e) => {
				           if (e.key === 'Enter' && props.onEnter) props.onEnter(parseIntOrUndefined(display) as any);
			           }}
			           disabled={(props.disabled || !props.onChange) ?? false}
			           inputMode={'numeric'}
			           sx={{...props.slotProps?.sx ?? {}}}
			/>
		</Tooltip>
	);
}

// Float (decimal) input
export function FloatInputEntry(props: InputEntryProps & { clamp?: { min?: number, max?: number } }) {
	const display = props.value !== undefined && props.value !== null ? String(props.value) : '';
	const parseFloatOrUndefined = (s: string): number | undefined => {
		const clean = s.replace(/[^\d,.-]/g, '');
		if (clean.trim() === '' || clean === '-' || clean === '.' || clean === ',') return undefined;
		const normalized = clean.replace(/,/g, '.');
		const n = Number.parseFloat(normalized);
		if (Number.isNaN(n)) return undefined;
		const min = (props as any).clamp?.min ?? -Infinity;
		const max = (props as any).clamp?.max ?? Infinity;
		return Math.max(min, Math.min(max, n));
	};
	return (
		<Tooltip title={props.slotProps && props.slotProps.tooltip ? props.slotProps.tooltip : ''}>
			<TextField fullWidth
			           inputMode={'decimal'}
			           {...props.slotProps?.textFieldProps}
			           label={props.label ?? ''}
			           required={props.required}
			           value={display}
			           onChange={(e) => props.onChange && props.onChange(parseFloatOrUndefined(e.target.value) as any)}
			           onKeyDown={(e) => {
				           if (e.key === 'Enter' && props.onEnter) props.onEnter(parseFloatOrUndefined(display) as any);
			           }}
			           disabled={(props.disabled || !props.onChange) ?? false}
			           sx={{...props.slotProps?.sx ?? {}}}
			/>
		</Tooltip>
	);
}

// Percent input (0-100)
export function PercentInputEntry(props: InputEntryProps) {
	return <FloatInputEntry {...props as any} clamp={{min: 0, max: 100}}/>
}

// Number or Percent: allow raw string like "10" or "10%"; keep as string
export function NumberOrPercentInputEntry(props: InputEntryProps) {
	const value = props.value != null ? String(props.value) : '';
	return (
		<Tooltip title={props.slotProps && props.slotProps.tooltip ? props.slotProps.tooltip : ''}>
			<TextField fullWidth
			           {...props.slotProps?.textFieldProps}
			           label={props.label ?? ''}
			           required={props.required}
			           value={value}
			           onChange={(e) => props.onChange && props.onChange(e.target.value)}
			           onKeyDown={(e) => {
				           if (e.key === 'Enter' && props.onEnter) props.onEnter(value);
			           }}
			           disabled={(props.disabled || !props.onChange) ?? false}
			           sx={{...props.slotProps?.sx ?? {}}}
			/>
		</Tooltip>
	);
}

// Date input using MUI DatePicker; maps to Dayjs
export function DateInputEntry(props: InputEntryProps) {
	const toDayjs = (v?: string | number | boolean | Dayjs): Dayjs | null => {
		if (!v) return null;
		if (typeof v === 'string') {
			const d = dayjs(v);
			return d.isValid() ? d : null;
		}
		if (typeof v === 'number' || typeof v === 'boolean') return null;
		const d = dayjs(v as any);
		return d.isValid() ? d : null;
	};
	const value = toDayjs(props.value as any);

	const handleChange = (newValue: Dayjs | null) => {
		if (!props.onChange) return;
		props.onChange(newValue ?? undefined);
	};

	// Merge text field props and allow Enter handling
	const mergedTextFieldProps: Partial<React.ComponentProps<typeof TextField>> = {
		fullWidth: true,
		required: props.required,
		...props.slotProps?.textFieldProps,
		onKeyDown: (e: any) => {
			props.slotProps?.textFieldProps?.onKeyDown?.(e);
			if (e.key === 'Enter' && props.onEnter) props.onEnter(value ?? undefined);
		},
	};

	return (
		<Tooltip title={props.slotProps && props.slotProps.tooltip ? props.slotProps.tooltip : ''}>
			<Grid component={'span'}>
				<DatePicker
					format={"YYYY-MM-DD"}
					label={props.label ?? ''}
					value={value}
					onChange={handleChange}
					disabled={(props.disabled || !props.onChange) ?? false}
					sx={{...props.slotProps?.sx ?? {}}}
					slotProps={{textField: mergedTextFieldProps}}
				/>
			</Grid>
		</Tooltip>
	);
}
